/**
 * BillAllocationModal.tsx
 * Bill-by-Bill payment / receipt allocation panel (Tally-style)
 *
 * Shows pending invoices for the selected party and lets the user
 * allocate the current payment / receipt amount across them.
 *
 * Props:
 *   partyName     – name of the Debtor / Creditor
 *   totalAmount   – total amount being paid/received
 *   voucherType   – 'Receipt' | 'Payment'
 *   companyId     – active company id
 *   onAccept(allocs) – called with final allocations array
 *   onSkip()      – called when user wants to save without allocating
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';

const fmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Bill {
  billId:   number;
  billNo:   string;
  billDate: string;
  amount:   number;   // original invoice amount
  paid:     number;   // already allocated in other vouchers
  balance:  number;   // outstanding
}

interface BillAllocationModalProps {
  partyName:    string;
  totalAmount:  number;
  voucherType:  string;  // 'Receipt' or 'Payment'
  companyId:    number;
  onAccept: (allocs: { billId: number; billNo: string; billDate: string; allocAmount: number; type: 'against_ref' | 'on_account' }[]) => void;
  onSkip: () => void;
}

export const BillAllocationModal = ({
  partyName, totalAmount, voucherType, companyId, onAccept, onSkip,
}: BillAllocationModalProps) => {
  // Determine invoice types to look up
  const invoiceTypes  = voucherType === 'Receipt' ? ['Sales', 'Debit Note'] : ['Purchase', 'Credit Note'];
  const settlementTypes = voucherType === 'Receipt' ? ['Receipt'] : ['Payment'];

  // ── Load outstanding bills from Dexie ─────────────────────────────────────
  const bills = useLiveQuery(async (): Promise<Bill[]> => {
    // Find party ledger
    const partyLedger = await db.ledgers
      .where('[companyId+name]').equals([companyId, partyName]).first();
    if (!partyLedger) return [];

    // All invoices for this party
    const allEntries = await db.voucherEntries.where('ledgerId').equals(partyLedger.id!).toArray();
    const invoiceVoucherIds = new Set<number>();
    allEntries.forEach(e => invoiceVoucherIds.add(e.voucherId));

    const candidateVouchers = invoiceVoucherIds.size > 0
      ? await db.vouchers.where('id').anyOf([...invoiceVoucherIds])
          .filter(v => invoiceTypes.includes(v.type) && !v.deleted)
          .toArray()
      : [];

    // All settlement vouchers with existing bill allocations
    const settlementVouchers = await db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => settlementTypes.includes(v.type) && !v.deleted && !!(v as any).billAllocations?.length)
      .toArray();

    // Build paid-map from existing allocations
    const paidMap = new Map<number, number>();
    settlementVouchers.forEach(sv => {
      ((sv as any).billAllocations || []).forEach((alloc: any) => {
        paidMap.set(alloc.billId, (paidMap.get(alloc.billId) || 0) + alloc.allocAmount);
      });
    });

    // Build bill list with balance
    const billList: Bill[] = candidateVouchers.map(v => {
      const paid = paidMap.get(v.id!) || 0;
      // Get invoice total from entries
      const vEntries = allEntries.filter(e => e.voucherId === v.id!);
      const partyEntry = vEntries.find(e => e.ledgerId === partyLedger.id!);
      const amount = partyEntry?.amount || 0;
      const balance = Math.max(0, amount - paid);
      return {
        billId:   v.id!,
        billNo:   v.referenceNumber || v.voucherNumber || String(v.id),
        billDate: v.date,
        amount,
        paid,
        balance,
      };
    }).filter(b => b.balance > 0.005)
      .sort((a, b) => a.billDate.localeCompare(b.billDate));

    return billList;
  }, [companyId, partyName]);

  // ── Local allocation state ─────────────────────────────────────────────────
  const [applied, setApplied] = useState<Record<number, string>>({});
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [focusedBill, setFocusedBill] = useState<number | null>(null);

  // Auto-allocate: fill oldest first
  const autoAllocate = useCallback(() => {
    if (!bills) return;
    let remaining = totalAmount;
    const newApplied: Record<number, string> = {};
    for (const b of bills) {
      if (remaining <= 0) break;
      const apply = Math.min(b.balance, remaining);
      if (apply > 0) newApplied[b.billId] = apply.toFixed(2);
      remaining -= apply;
    }
    setApplied(newApplied);
  }, [bills, totalAmount]);

  // Auto-allocate on first load
  useEffect(() => {
    if (bills && bills.length > 0 && Object.keys(applied).length === 0) autoAllocate();
  }, [bills]);

  // Focus first input on mount
  useEffect(() => {
    if (bills && bills.length > 0) {
      setTimeout(() => inputRefs.current[bills[0]?.billId]?.focus(), 80);
    }
  }, [bills]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onSkip(); return; }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault(); handleAccept(); return;
    }
    if (e.altKey && e.key.toLowerCase() === 'a') { e.preventDefault(); autoAllocate(); return; }
  }, [onSkip, applied, bills]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  // Totals
  const totalApplied: number = (Object.values(applied) as string[]).reduce((s: number, v: string) => s + (parseFloat(v) || 0), 0 as number);
  const onAccount: number    = Math.max(0, totalAmount - totalApplied);
  const overApplied: boolean = totalApplied > totalAmount + 0.005;

  const handleAccept = () => {
    const allocs: any[] = [];
    (bills || []).forEach(b => {
      const a = parseFloat(applied[b.billId] || '0');
      if (a > 0) allocs.push({ billId: b.billId, billNo: b.billNo, billDate: b.billDate, allocAmount: a, type: 'against_ref' });
    });
    if (onAccount > 0.005) {
      allocs.push({ billId: 0, billNo: 'On Account', billDate: '', allocAmount: parseFloat(onAccount.toFixed(2)), type: 'on_account' });
    }
    onAccept(allocs);
  };

  const th = 'px-2 py-1 text-left font-bold text-[11px] text-[#1e8585] border-b border-[#aebec8] bg-[#f0f9f9]';
  const thR = `${th} text-right`;
  const td = 'px-2 py-[3px] border-b border-gray-100 text-[11px]';
  const tdR = `${td} text-right tabular-nums`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(1px)' }}>
      <div className="bg-white rounded-lg shadow-2xl w-[720px] max-h-[85vh] flex flex-col border-2 border-[#1e8585]">

        {/* Header */}
        <div className="bg-[#1e8585] text-white px-4 py-2 flex items-center justify-between shrink-0 rounded-t-lg">
          <div>
            <div className="font-bold text-[13px]">Bill-by-Bill Allocation</div>
            <div className="text-[10px] text-teal-200">
              {voucherType === 'Receipt' ? 'Receipt from' : 'Payment to'}: <span className="font-semibold text-white">{partyName}</span>
              <span className="ml-3">Amount: ₹{fmt(totalAmount)}</span>
            </div>
          </div>
          <div className="text-[10px] text-teal-200 text-right">
            <div>Alt+A: Auto-allocate</div>
            <div>Ctrl+A: Accept  •  Esc: Skip</div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          {!bills ? (
            <div className="p-8 text-center text-gray-400">Loading pending bills...</div>
          ) : bills.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 text-sm mb-2">No pending invoices found for <strong>{partyName}</strong>.</div>
              <div className="text-gray-400 text-xs">The entire amount of ₹{fmt(totalAmount)} will be recorded <strong>On Account</strong>.</div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead className="sticky top-0">
                <tr>
                  <th className={th}>Invoice No.</th>
                  <th className={th}>Date</th>
                  <th className={thR}>Invoice Amt (₹)</th>
                  <th className={thR}>Already Paid (₹)</th>
                  <th className={thR}>Pending (₹)</th>
                  <th className={thR}>Apply Now (₹)</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b, i) => {
                  const isFocused = focusedBill === b.billId;
                  return (
                    <tr key={b.billId} className={isFocused ? 'bg-[#f0fafa]' : i % 2 === 0 ? 'bg-white' : 'bg-[#fafcfc]'}>
                      <td className={`${td} font-mono text-[10px] font-semibold text-[#1e8585]`}>{b.billNo}</td>
                      <td className={td}>{b.billDate}</td>
                      <td className={tdR}>{fmt(b.amount)}</td>
                      <td className={`${tdR} text-gray-400`}>{b.paid > 0 ? fmt(b.paid) : '—'}</td>
                      <td className={`${tdR} font-semibold text-amber-700`}>{fmt(b.balance)}</td>
                      <td className="px-1 py-[2px] border-b border-gray-100">
                        <input
                          ref={el => { inputRefs.current[b.billId] = el; }}
                          type="number"
                          min={0}
                          max={b.balance}
                          step="0.01"
                          value={applied[b.billId] ?? ''}
                          placeholder="0.00"
                          onFocus={() => setFocusedBill(b.billId)}
                          onBlur={() => setFocusedBill(null)}
                          onChange={e => {
                            const val = e.target.value;
                            setApplied(prev => ({ ...prev, [b.billId]: val }));
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              const nextBill = bills[i + 1];
                              if (nextBill) inputRefs.current[nextBill.billId]?.focus();
                              else handleAccept();
                            }
                            if (e.key === 'ArrowUp' && i > 0) {
                              e.preventDefault();
                              inputRefs.current[bills[i - 1].billId]?.focus();
                            }
                          }}
                          className="w-full text-right tabular-nums text-[11px] font-semibold text-[#1e8585] border border-[#aebec8] rounded px-1 py-[2px] outline-none focus:border-[#1e8585] focus:bg-[#f0fafa]"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Totals + Actions */}
        <div className="border-t-2 border-[#aebec8] bg-[#f8fcfc] px-4 py-3 shrink-0 rounded-b-lg">
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="text-center">
              <div className="text-[9px] text-gray-500 uppercase tracking-wide">Total Amount</div>
              <div className="font-bold text-[14px] font-mono">₹{fmt(totalAmount)}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500 uppercase tracking-wide">Applied to Bills</div>
              <div className={`font-bold text-[14px] font-mono ${overApplied ? 'text-red-600' : 'text-[#1e8585]'}`}>₹{fmt(totalApplied)}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500 uppercase tracking-wide">On Account</div>
              <div className={`font-bold text-[14px] font-mono ${onAccount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>₹{fmt(onAccount)}</div>
            </div>
          </div>

          {overApplied && (
            <div className="text-xs text-red-600 text-center mb-2 font-semibold">
              ⚠ Applied amount (₹{fmt(totalApplied)}) exceeds total (₹{fmt(totalAmount)}). Please reduce allocations.
            </div>
          )}

          <div className="flex gap-2 justify-center">
            <button onClick={autoAllocate}
              className="px-4 py-1.5 text-[11px] font-bold border border-[#1e8585] text-[#1e8585] rounded hover:bg-[#f0fafa] transition-colors">
              Auto-allocate (Alt+A)
            </button>
            <button onClick={() => setApplied({})}
              className="px-4 py-1.5 text-[11px] font-bold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors">
              Clear All
            </button>
            <button onClick={onSkip}
              className="px-4 py-1.5 text-[11px] font-bold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors">
              Skip (Esc)
            </button>
            <button onClick={handleAccept} disabled={overApplied}
              className={`px-6 py-1.5 text-[11px] font-bold rounded transition-colors ${overApplied ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#1e8585] text-white hover:bg-[#166b6b]'}`}>
              ✓ Accept (Ctrl+A)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
